import React, { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  useGLTF, useAnimations, OrbitControls, Caustics, AccumulativeShadows, ContactShadows, RandomizedLight, Html, Environment,
  MeshTransmissionMaterial, Bounds
} from '@react-three/drei'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import './styles.css';  // Add any styles for labels
import { EffectComposer, Bloom,BrightnessContrast, ChromaticAberration, N8AO } from '@react-three/postprocessing'
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { LineGeometry, LineMaterial, Line2, RGBELoader } from 'three-stdlib';
import { XR, useXRHitTest, createXRStore } from '@react-three/xr';
import { gsap } from "gsap";
import QRCode from 'qrcode';
import { Spinner } from 'spin.js';
import Modal from 'react-modal';

import { useShowCorrectCoordinates } from "./functions/useShowCorrectCoordinates";
import { CreateCoordinates } from "./functions/CreateCoordinates";
import exampleSofaDimensions from "./functions/ExampleSofaDimensions.json";
import exampleScrewDimensions from "./functions/ExampleScrewDimensions.json";

// import all the components
import { ARModel } from "./components/ARModel";
import { Arrowhead } from "./components/ArrowHead";
import { DimensionLine } from "./components/DimensionLine";
import { DimensionLabel } from "./components/DimensionLabel";

// const Canvas = lazy(() => import('@react-three/fiber'));

function AutoFitCamera({ cameraOffset, modelRef }) {
  const { camera } = useThree(); // Access camera and renderer
  const controlsRef = useRef();

  useEffect(() => {
    if (modelRef.current) {
      console.log("Model ref loaded:", modelRef.current); // Debugging model ref

      // Get the bounding box of the model
      const box = new THREE.Box3().setFromObject(modelRef.current);
      const size = box.getSize(new THREE.Vector3()); // Size of the model
      const center = box.getCenter(new THREE.Vector3()); // Center of the model

      // Debugging: Log the bounding box, size, and center
      console.log("Bounding Box:", box);
      console.log("Model Size:", size);
      console.log("Model Center:", center);

      // Calculate the max dimension (width, height, depth)
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180); // Convert vertical fov to radians

      console.log("CameraFOV: " + camera.fov);

      // Calculate the distance the camera needs to be from the object to fit it all
      const cameraDistance = maxDim / (2 * Math.tan(fov / 2));

      // Debugging: Log the calculated camera distance
      console.log("Calculated Camera Distance:", cameraDistance);

      // Update the camera position
      camera.position.set(
        -size.z,
        size.y,
        center.z + cameraDistance * cameraOffset
      );

      // Optional: Make sure the controls target the center of the object
      if (controlsRef.current) {
        controlsRef.current.maxDistance = cameraDistance * cameraOffset;
        controlsRef.current.minDistance = cameraDistance;
        controlsRef.current.target.set(center.x, center.y, center.z);
        controlsRef.current.update(); // Required to re-render the scene
      }

      camera.lookAt(center); // Ensure the camera is looking at the model
      camera.updateProjectionMatrix(); // Update the camera projection

      // Debugging: Log the new camera position
      console.log("Updated Camera Position:", camera.position);
    }
  }, [camera, modelRef]);

  return <OrbitControls ref={controlsRef} />;
}

function ThreeScene({
  params,
  cameraOffset,
  line_width,
  showLabels,
  animationPlaying,
  isAnimToggled,
  //animationDirection,
  setHasAnimations,
  variantFileGlb,
  setGltfSceneFiltered,
  variantFileUsd,
  variantFilesHdr,
  variantEnvironmentsHdr,
  yawAngle,
  pitchAngle,
  aspectRatio,
  objectDimensions,
  parentWidth,
  parentHeight,
  windowHeight,
  windowWidth,
  sceneBackgroundColour,
  isSmallObject,
  dimensionsOnTop,
  productSettings,
  isMobile,
  configuratorEnabled,
  onMeshClicked,
  setLoadingLightMap,
}) {
  const mixerRef = useRef();
  const controls = useRef();
  const modelRef = useRef();
  // Access camera, scene, and WebGL renderer using useThree hook
  const { camera } = useThree();

  const [coordinateData, setCoordinateData] = useState(null);

  const gltfSceneRef = useRef();
  const dimensionsMapRef = useRef(new Map());
  const [dimensions, setDimensions] = useState();
  const [box, setBox] = useState();
  const [center, setCenter] = useState();
  const [size, setSize] = useState();

  const [isCameraSettled, setIsCameraSettled] = useState(false);
  const [lightMaps, setLightMaps] = useState({});

  const [lines, setLines] = useState([]);
  //const dimensionsMap = new Map();

  //TODO: If configuratorEnabled, then use default configuration to show specific nodes only
  //console.log('model:', params.modelName);
  console.log("variant glb:", variantFileGlb[0]);
  const { scene: gltfScene, animations } = useGLTF(
    variantFileGlb[0],
    // process.env.PUBLIC_URL + "/" + params.modelName,
    (loader) => {
      loader.setCrossOrigin("anonymous");
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        "https://cdn.jsdelivr.net/npm/three@0.153.0/examples/jsm/libs/draco/gltf/"
      );
      loader.setDRACOLoader(dracoLoader);
    },
    undefined,
    (error) => {
      console.error("Error loading GLTF model:", error);
    }
  );
  console.log("GLTF SCENE:", gltfScene);

  const minAxisIndex = (box) => {
    const boxSize = new THREE.Vector3();
    box.getSize(boxSize);
    return boxSize.x <= boxSize.y && boxSize.x <= boxSize.z
      ? 0
      : boxSize.y <= boxSize.z
      ? 1
      : 2;
  };

  const maxAxisIndex = (box) => {
    const boxSize = new THREE.Vector3();
    box.getSize(boxSize);
    return boxSize.x >= boxSize.y && boxSize.x >= boxSize.z
      ? 0
      : boxSize.y >= boxSize.z
        ? 1
        : 2;
  }

  const [hovered, setHovered] = useState(null);
  //const { scene } = useThree();

  const handlePointerOver = (event) => {
    event.stopPropagation();

    const mesh = event.object;
    if (!mesh) return;


    
    console.log('mesh hovered:', mesh);

    // Find the mesh inside gltfScene
    const sceneMesh = findMeshInScene(mesh, gltfScene);
    if (!sceneMesh) {
        console.warn("Mesh not found in gltfScene");
        return;
    }

    const topLevelMesh = getTopLevelMesh(sceneMesh);
    console.log("Top-level mesh:", topLevelMesh);

    // Traverse through all children of the top-level mesh
    topLevelMesh.traverse((child) => {
      if (child.isMesh) {
        console.log('child mesh:', child);
          // Clone material only if not already cloned
          if (!child.userData.originalMaterial) {
              child.userData.originalMaterial = child.material; // Store original
              child.material = child.material.clone(); // Clone for unique highlight
          }

          if (!child.material.emissive) return;
          child.material.emissive.setHex(0xffffff); // Apply highlight
      }
  });

  setHovered(topLevelMesh);

    // Clone the material if it hasn't been cloned already
    // if (!mesh.userData.originalMaterial) {
    //   mesh.userData.originalMaterial = mesh.material; // Store original material
    //   mesh.material = mesh.material.clone(); // Clone the material
    // }

    // setHovered(mesh);
    // mesh.material.emissive.setHex(0xffffff); // Glow effect
  };

  const handlePointerOut = (event) => {
    event.stopPropagation();

    const mesh = event.object;
    if (!mesh) return;

    // Find the mesh inside gltfScene
    const sceneMesh = findMeshInScene(mesh, gltfScene);
    if (!sceneMesh) return;

    // Get the top-level mesh
    const topLevelMesh = getTopLevelMesh(sceneMesh);

    // Restore materials for all children
    topLevelMesh.traverse((child) => {
        if (child.isMesh && child.userData.originalMaterial) {
            child.material = child.userData.originalMaterial; // Restore original
            delete child.userData.originalMaterial;
        }
    });

    // if (mesh.userData.originalMaterial) {
    //   mesh.material = mesh.userData.originalMaterial; // Restore original material
    //   delete mesh.userData.originalMaterial; // Remove reference
    // }

    setHovered(null);
  };

  const getTopLevelMesh = (mesh) => {
    while (mesh.parent && mesh.parent.name !== "Studio") {
      mesh = mesh.parent; // Move up the hierarchy
    }
    return mesh; // Return the top-level mesh with 'Scene' as parent
  };

  const findMeshInScene = (mesh, scene) => {
    let foundMesh = null;

    scene.traverse((child) => {
      if (child === mesh) {
        foundMesh = child;
      }
    });

    return foundMesh;
  };

  const handleClick = (event) => {
    event.stopPropagation();
    const mesh = event.object;
    console.log("mesh clicked:", mesh);
    if (!mesh) return;

    // Find the mesh inside gltfScene
    const sceneMesh = findMeshInScene(mesh, gltfScene);
    if (!sceneMesh) {
        console.warn("Mesh not found in gltfScene");
        return;
    }

    // Find the top-level mesh (with 'Scene' as parent)
    const topLevelMesh = getTopLevelMesh(sceneMesh);
    console.log('Top-level mesh:', topLevelMesh);

    // Compute the bounding box of the clicked mesh
    const box = computeScaledBoundingBox(topLevelMesh);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    // Calculate zoom factor based on the mesh size
    // const sizeFactor = size.length() * params.zoomMultiplier;
    // const minZoom = Math.min(Math.max(sizeFactor, 0.1) * params.zoomClose, params.maxZoomClose);
    // const maxZoom = size.length() * params.maxZoomFactor;
    const targetZoom =
      size.length() * params.maxZoomFactor * params.zoomOnPartMultiplier;

    console.log("Zooming to:", center, "Zoom:", targetZoom);

    // Animate the camera position and zoom
    if (controls.current && camera) {
      gsap.to(camera.position, {
        duration: 1,
        x: center.x,
        y: center.y,
        z: center.z + targetZoom, // Adjust Z to move closer
        ease: "power2.out",
        onUpdate: () => controls.current.update(),
      });

      gsap.to(controls.current.target, {
        duration: 1,
        x: center.x,
        y: center.y,
        z: center.z,
        ease: "power2.out",
        onUpdate: () => controls.current.update(),
      });
    }

    if (onMeshClicked) {
      onMeshClicked(topLevelMesh.name);
    }
  };


  const findHdrMatchingMeshName = (meshName, gltfScene) => {
    // Extract the keyword from meshName
    const match = meshName.match(/_([^_]+)\.hdr$/);
    if (!match) {
        console.warn("Invalid variant file format:", meshName);
        return null;
    }
    const keyword = match[1]; // Extracted keyword
    console.log("Extracted Keyword:", keyword);

    // Traverse first-level children of gltfScene to find a match
    // for (const child of gltfScene.children) {
    //   console.log('child found, keyword', child, keyword);
    //   if (child.isMesh && child.name.includes(keyword)) {
    //     console.log(`Match found: ${keyword} in ${child.name}`);
    //     return child.name; // Return full mesh name
    //   }
    // }

    return keyword;
};


  //console.log('GLTF scene:', gltfScene);
  // Call useAnimations unconditionally, providing a fallback empty array if animations is null or undefined
  const { actions, mixer } = useAnimations(animations || [], gltfScene);

  // // Then, set actions and mixer to null if animations is missing
  const animationActions = animations ? actions : null;
  const animationMixer = animations ? mixer : null;

  const GetObjectExtDimensions = (box) => {
    const positions = {
      backBottomLeft: new THREE.Vector3(box.min.x, box.min.y, box.min.z),
      frontBottomLeft: new THREE.Vector3(box.min.x, box.min.y, box.max.z),
      backBottomRight: new THREE.Vector3(box.max.x, box.min.y, box.min.z),
      frontBottomRight: new THREE.Vector3(box.max.x, box.min.y, box.max.z),
      topLeft: new THREE.Vector3(
        box.min.x,
        box.max.y,
        box.min.z
      ),
    };
    // console.log('first arrow start, end:', positions.backBottomLeft, positions.frontBottomLeft2);
    // console.log('second arrow start, end:', positions.frontBottomLeft1, positions.frontBottomRight1);
    // console.log('third arrow start, end:', positions.backBottomLeft, positions.topLeft);

    const toCoordStr = (v) => `${v.x.toFixed(2)*100}, ${v.y.toFixed(2)*100}, ${v.z.toFixed(2)*100}`;

    const arrowPositioning = [
      {
        start: toCoordStr(positions.frontBottomLeft),
        end: toCoordStr(positions.backBottomLeft),
        offset: 0,
        "offsetDirection": "x",
      },
      {
        start: toCoordStr(positions.frontBottomRight),
        end: toCoordStr(positions.frontBottomLeft),
        offset: 0,
        "offsetDirection": "y",
      },
      {
        start: toCoordStr(positions.topLeft),
        end: toCoordStr(positions.backBottomLeft),
        offset: 0,
        "offsetDirection": "z",
      },
    ];

    return {
      modelSetup: {
        units: "cm",
        origin: "center",
      },
      arrowPositioning,
    };
  };

  // Check for animations and set hasAnimations accordingly
  useEffect(() => {
    if (animations && animations.length > 0) {
      console.log("Animations found:", animations); // Debugging
      setHasAnimations(true); // Update state to indicate animations are available
    } else {
      console.log("No animations found"); // Debugging
      setHasAnimations(false); // No animations available
    }
  }, [animations, setHasAnimations]);

  useEffect(() => {
    console.log('variant files hdr:', variantFilesHdr);
    if (!variantFilesHdr || variantFilesHdr.length === 0) return; //Or is it .size?
    
    const loadedMaps = {};
    let loadedCount = 0;
    
    variantFilesHdr.forEach((file, index) => {
      const rgbeLoader = new RGBELoader();
      rgbeLoader.load(
        file,
        (texture) => {
          // Configure the texture properly for lightmapping
          texture.flipY = false;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.colorSpace = THREE.NoColorSpace; // For non-color data like lightmaps
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false; // Often better for lightmaps
          texture.channel = 1;
          
          //Match name with mesh name
          const targetMeshBase = findHdrMatchingMeshName(file, gltfScene);

          console.log('texture, targetMeshBaseName, intensity, loadedCount', texture, targetMeshBase, params.lightMapIntensity, loadedCount);
          
          // Store the loaded texture with its configuration
          loadedMaps[index] = {
            texture: texture,
            targetMeshBaseName: targetMeshBase,
            intensity: params.lightMapIntensity
          };
          
          loadedCount++;
          
          // When all lightmaps are loaded, update the state
          if (loadedCount === variantFilesHdr.length) {
            console.log('Loaded maps:', loadedMaps);
            setLightMaps(loadedMaps);
          }
        },
        undefined,
        (error) => {
          console.error(`Error loading lightmap ${index}:`, error);
          loadedCount++;
          
          // Continue even if some lightmaps fail to load
          if (loadedCount === variantFilesHdr.length) {
            setLightMaps(loadedMaps);
          }
        }
      );
    });
  }, [variantFilesHdr, params.lightMapIntensity]);

  useEffect(() => {
    if (!gltfScene) return;
    if (variantFilesHdr.length == 0) {
      setLoadingLightMap(false);
      return;
    } 
    if (Object.keys(lightMaps).length === 0) return;

    console.log('Applying lightmaps to meshes...');
    
    // Create a Map for quick lookup of lightmap textures
    const lightMapLookup = new Map();
    Object.values(lightMaps).forEach(({ targetMeshBaseName, texture }) => {
        if (targetMeshBaseName) {
            lightMapLookup.set(targetMeshBaseName.toLowerCase(), texture);
        }
    });

    console.log('LightMap Lookup:', lightMapLookup);

    // IMPORTANT: Check if materials are being shared between meshes
    console.log('=== Material Sharing Analysis ===');
    const materialMap = new Map();

    gltfScene.traverse((child) => {
      if (!child.isMesh) return;
      //if (child.material && child.material.color) child.material.color.set(0, 0, 1);
        // Store material references to check for sharing
        if (!materialMap.has(child.material.uuid)) {
          materialMap.set(child.material.uuid, []);
        }
        materialMap.get(child.material.uuid).push(child.name);
        
        console.log(`Mesh: "${child.name}", Material UUID: ${child.material.uuid}`);
      }
    );
    
    // Log which materials are shared
    console.log('=== Shared Materials ===');
    materialMap.forEach((meshNames, materialUUID) => {
      if (meshNames.length > 1) {
        console.log(`Material ${materialUUID} is shared by:`, meshNames);
      }
    });

    console.log('=== Cloning Materials ===');
    // Traverse the gltfScene **once** and apply the correct lightmap
    // Clone materials to ensure each mesh has its own material instance
    gltfScene.traverse((child) => {
      if (!child.isMesh) return;

      const childNameLower = child.name.toLowerCase();

      if (child.isMesh && childNameLower.includes("floor")) {
        if (child.material) {
          // Clone the material to avoid affecting other objects
          child.material = child.material.clone();
          
          // Fix for alpha map visibility
          child.material.transparent = true;
          // child.material.alphaTest = 0.001; // Adjust this value (0.01-0.5) to change shadow intensity
          
          // This is critical - change how alpha blending works
          // child.material.blending = THREE.MultiplyBlending; // Try different blending modes
          // Other modes to try: THREE.NormalBlending, THREE.AdditiveBlending
          
          child.material.needsUpdate = true;
          console.log("Modified floor material:", child.material);
        }
      }

      // Find a matching lightmap based on the child's name
      for (const [baseName, texture] of lightMapLookup) {
          if (childNameLower.includes(baseName)) {
              // Clone material only if we need to modify it
              console.log(`Applying "${baseName}" lightmap to: "${child.name}"`);
              // Clone the material to ensure it's not shared
              child.material = child.material.clone();
              console.log(`Cloned material for: "${child.name}", new UUID: ${child.material.uuid}`);
              child.material.lightMap = texture;
              child.material.lightMapIntensity = params.lightMapIntensity;
              child.material.needsUpdate = true;

              console.log(`Applied lightmap to: "${child.name}"`);
              break; // No need to check further once matched
          }
      }
    });

    setLoadingLightMap(false);

  }, [gltfScene, lightMaps, params.lightMapIntensity]);

  // // Store reference to the mixer for animation control
  useEffect(() => {
    if (mixer) {
      mixerRef.current = mixer;
    }
  }, [mixer]);

  // Start or stop animations when the button is clicked
  //TODO: Move the animation button to the buttom right
  useEffect(() => {
    if (!isAnimToggled) return;
    if (actions && animations && animations.length > 0) {
      const action = actions[Object.keys(actions)[0]];
      if (animationPlaying) {
        if (!productSettings.reversableAnimation){
          action.loop = THREE.LoopRepeat; // default looping for non-reversible animation
          actions[Object.keys(actions)[0]]?.play(); // Play the first animation
        }
        else {
          action.reset();
          // Set the animation to play only once:
          action.loop = THREE.LoopOnce;
          action.clampWhenFinished = true;
          action.time = 0;
          action.timeScale = 1;
          action.play();
        }
      } else if (!productSettings.reversableAnimation){
        action.stop(); // Stop the animation
      }
      else {
        action.reset();
        action.loop = THREE.LoopOnce;
        action.clampWhenFinished = true;
        action.time = action.getClip().duration;
        action.timeScale = -1;
        action.play();
      }
    }
  }, [animationPlaying, actions, animations]);

  //TODO: Use bounding box to set dimensions if it's missing in objectDimensions
  useEffect(() => {
    // Create coordinates for dimension vectors and labels
    const fetchDimensions = async () => {
      // const exampleSofaDimensions = await fetch("/components/ExampleSofaDimensions.json")
      //   .then((response) => response.json());
      // console.log("ExampleSofaDimensions:", exampleSofaDimensions);

      let tempObjectDimensions = objectDimensions;
      let isCalculated = false;
      if (!tempObjectDimensions && box) {
        tempObjectDimensions = GetObjectExtDimensions(box);
        isCalculated = true;
      }
      const coordinateData = CreateCoordinates(size, isCalculated, {
        ObjectDimensions: tempObjectDimensions,
      });
      setCoordinateData(coordinateData);
    };
    fetchDimensions();
  }, [size]);

  const { positionAndLabelOutput } = useShowCorrectCoordinates(
    showLabels,
    center,
    { coordinateData }
  );

  useEffect(() => {
    if (gltfScene) {

      console.log('GLTF Scene Loaded:', gltfScene);  // Debugging model loading
      // Traverse through the GLTF scene and set all meshes to cast and receive shadows
      gltfScene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = productSettings.isBaked ? false : true;  // Enable casting shadows
          child.receiveShadow = productSettings.isBaked ? false : true;  // Enable receiving shadows
        }
      });

      modelRef.current = gltfScene;

      console.log('MODEL REF:', modelRef.current);

      const meshBox = computeScaledBoundingBox(gltfScene);

      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      meshBox.getCenter(center);
      meshBox.getSize(size);
      setCenter(center);
      setSize(size);
      setBox(meshBox);
    }
  }, [gltfScene]);

  if (gltfScene) {
    gltfScene.traverse((node) => {
      if (node.isMesh && node.material) {
        const material = node.material;
        // Check if custom properties exist in material's userData
        if (material.userData && material.userData.extras) {
          console.log("Custom Material Properties:", material.userData.extras);

          if (material.userData.extras.objectType === "jewellery") {
            params.modelScale = 10;
            params.environmentIntensity = 1;
            params.enablePan = true;
            params.fullAngle = true;
          } else {
            //Non exclusive if statements so we can accept several tags, glass and reflective override fabrics
            if (material.userData.extras.materialType === "fabric") {
              console.log("fabrics");
              params.environmentIntensity = 0;
            }
            if (material.userData.extras.surfaceType === "reflective") {
              console.log("reflective");
              params.environmentIntensity = 1;
            }
            if (material.userData.extras.surfaceType === "glass") {
              console.log("glass");
              params.environmentIntensity = 1;
              params.enablePan = true;
              params.fullAngle = true;
            }
            if (material.userData.extras.camType === "freeCam") {
              console.log("free cam");
              params.enablePan = true;
              params.fullAngle = true;
            }
          }
        }
      }
    });
  }

  // Calculate new x and z positions for the spotlight in a circular path
  const x = params.lightRadius * Math.cos(params.light_position); // X-coordinate
  const z = params.lightRadius * Math.sin(params.light_position); // Z-coordinate

  //Shift box if the box min vertical axis isn't at 0
  const computeCorrectedBoundingBox = (box, object) => {
    if (!object) return;

    // Correct only if the center is off
    if (box.min.y !== 0) {
      const offset = new THREE.Vector3(0, -box.min.y, 0);
      box.min.add(offset);
      box.max.add(offset);
  }
    return box;
  };

  const computeScaledBoundingBox = (object) => {
    const box = new THREE.Box3();
    const originalBox = new THREE.Box3().setFromObject(object);
    const originalCenter = new THREE.Vector3();
    originalBox.getCenter(originalCenter);


    //TODO: Check if this always works
    let isMeshContainsFloor = false;
    gltfScene.traverse((child) => {
      if (child.name.toLowerCase().includes("floor")) {
        isMeshContainsFloor = true;
      }
    });

    if (!isMeshContainsFloor) return new THREE.Box3().setFromObject(object);
  
    const traverseWithScale = (child, parentScale) => {
      // Compute total scale including inherited parent scale
      const totalScale = parentScale.clone().multiply(child.scale);
  
      if (child.isMesh && !child.name.toLowerCase().includes("floor")) {
  
        // Compute the bounding box for this mesh
        const childBox = new THREE.Box3().setFromObject(child);
  
        // Apply the total scale
        childBox.min.multiply(totalScale);
        childBox.max.multiply(totalScale);
  
        // Expand the overall bounding box
        box.expandByPoint(childBox.min);
        box.expandByPoint(childBox.max);
      }
  
      // Continue traversal for all children
      child.children.forEach((grandChild) => traverseWithScale(grandChild, totalScale));
    };
  
    // Start traversal with default scale (1,1,1)
    traverseWithScale(object, new THREE.Vector3(1, 1, 1));
  
    console.log('FINAL BOUNDING BOX:', box);
    return computeCorrectedBoundingBox(box, object);
  };
  
  

  useEffect(() => {
    // Update maxPolarAngle for labels, jewellery or freecam objects
    const fullAngle = params.fullAngle;
    if (!controls.current || !gltfScene) return;
    
    controls.current.maxPolarAngle = (showLabels || fullAngle) ? Math.PI : Math.PI / 2;
    console.log('max polar angle:', controls.current.maxPolarAngle);

    const updateZoomLimits = () => {
      const box = computeScaledBoundingBox(gltfScene);
      console.log('NEW BOX:', box);
      const scaleUpFactor = isSmallObject ? params.smallObjectScaleUp : 1;
      const scaledSize = new THREE.Vector3();
      box.getSize(scaledSize).multiplyScalar(scaleUpFactor); // Scale the size
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      box.getCenter(center);
      box.getSize(size);

      // Compute diagonal length of the bounding box
      const boundingDiagonal = size.length();

      // Determine ideal distance using field of view
      const fov = camera.fov * (Math.PI / 180); // Convert FOV to radians
      const aspectRatio = camera.aspect;
      const fitHeightDistance = boundingDiagonal / (2 * Math.tan(fov / 2));
      const fitWidthDistance = fitHeightDistance / aspectRatio;
      const idealDistance = Math.max(fitHeightDistance, fitWidthDistance) * 1.2; // Add padding

      //console.log('Ideal Camera Distance:', idealDistance);

      // Set camera position based on ideal distance
      const direction = new THREE.Vector3(0, 0, 1); // Default forward direction
      direction.applyQuaternion(camera.quaternion); // Align with camera orientation
      const newPosition = center.clone().addScaledVector(direction, idealDistance);

      camera.position.copy(newPosition);
      camera.lookAt(center);
      camera.updateProjectionMatrix();

      // Set zoom constraints
      const minZoom = Math.max(idealDistance * params.zoomClose, params.minZoomClose); 
      const maxZoom = idealDistance * params.maxZoomFactor; 

      console.log("min zoom:", minZoom, "max zoom:", maxZoom);

      // // Apply dynamic zoom limits
      controls.current.minDistance = minZoom;
      controls.current.maxDistance = maxZoom;
      // Set the control target to the center of the model
      controls.current.target.copy(center);
      controls.current.update();
    }

    // Initial zoom limits setup
    updateZoomLimits();

    const toggleFloorMeshesVisibility = (object, showLabels) => {
      if (object.isMesh && object.name.toLowerCase().includes("floor")) {
        object.visible = !showLabels; // Toggle visibility
        console.log(`Floor mesh '${object.name}' visibility set to: ${object.visible}`);
      }

      // Recursively check children
      object.children.forEach(child => toggleFloorMeshesVisibility(child, showLabels));
    };

    // Call the function on the root scene
    toggleFloorMeshesVisibility(gltfScene, showLabels);
    setIsCameraSettled(true);
    
  }, [showLabels, gltfScene, variantFileGlb]);

  console.log("parent width threescene:", parentWidth);
  console.log("isAndroid:", params.isAndroid);
  console.log('positionAndLabelOutput', positionAndLabelOutput);
  console.log('is baked:', productSettings.isBaked);
  console.log('variant environments hdr', variantEnvironmentsHdr);
  return (
    <>
      {/* Figure out why dimension line and model are offset */}
      <color attach="background" args={[sceneBackgroundColour]} />
      {/* <ambientLight intensity={params.amblight_intensity} color={params.lights_colour} /> */}
      {/* <Stage
          intensity={isSmallObject ? 2 : 0.5}
          preset="portrait"
          shadows={(!isSmallObject && !params.isAndroid) ? ({ type: 'contact', color: 'white', colorBlend: 10, opacity: 1}) : (false)}
          adjustCamera={1.5}
          environment={{ environmentIntensity: 1.25, files: studioHdrUrl }}
          style={{
            width: parentWidth,
            height: parentHeight
          }}
          >
            <Gltf 
            ref={gltfSceneRef} 
            scale={isSmallObject ? params.smallObjectScaleUp : 1} 
            castShadow={params.isAndroid? false : true} 
            receiveShadow={params.isAndroid? false : true} 
            src={variantFileGlb[0]} />
            <Gltf ref={gltfSceneRef} castShadow receiveShadow scale={isSmallObject ? params.smallObjectScaleUp : 1}  src={process.env.PUBLIC_URL + "/" + params.modelName} />
        </Stage> */}

      <Bounds
        clip
        observe
        margin={1.4}
        maxDuration={0.01}
      >
        <group>
          <primitive
            position={[0, 0, 0]}
            object={gltfScene}
            ref={modelRef}
            scale={isSmallObject ? params.smallObjectScaleUp : 1}
            visible={isCameraSettled}
            onPointerOver={(productSettings.enableConfigurator && configuratorEnabled) ? handlePointerOver : undefined}
            onPointerOut={(productSettings.enableConfigurator && configuratorEnabled) ? handlePointerOut : undefined}
            onClick={(productSettings.enableConfigurator && configuratorEnabled) ? handleClick : undefined}
            receiveShadow
          />
        </group>
      </Bounds>
      {/* minPolarAngle={0} maxPolarAngle={Math.PI / 1.9} */}
      <OrbitControls enablePan={params.enablePan} ref={controls} />
      {/* Ambient Light - general ambient light across the scene */}

      {/* Directional Light*/}
      {!productSettings.isBaked && (
        <directionalLight
          position={[0, 2, 1]}
          intensity={params.amblight_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Ambient light */}
      {productSettings.isBaked &&
        <>
          <directionalLight intensity={params.dirlightIntensity} position={[0, 1, 1]}/>
        </>
      }
      {/* Spotlight Rear */}
      {!productSettings.isBaked && (
        <spotLight
          position={[0, params.lightheight, -2]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Front */}
      {!productSettings.isBaked && (
        <spotLight
          position={
            productSettings.isBaked
              ? [0, params.lightheight, 3]
              : [0, params.lightheight, 1]
          }
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Right */}
      {!productSettings.isBaked && (
        <spotLight
          position={[-2, params.lightheight, 0]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid || productSettings.isBaked ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Left */}
      {!productSettings.isBaked && (
        <spotLight
          position={[2, params.lightheight, 0]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={productSettings.isBaked ? false : true}
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Spot */}
      {showLabels && !params.isAndroid && !productSettings.isBaked && (
        <spotLight
          position={[x, params.lightheight, z]}
          angle={params.lightAngle}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={productSettings.isBaked ? false : true}
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}
      {/* Spotlight Under */}
      {(!params.isAndroid || isSmallObject) && !productSettings.isBaked && (
        <spotLight
          position={[0, -params.lightheight, 0]}
          angle={Math.PI / 2}
          penumbra={params.spotpenumbra}
          decay={params.spotdecay}
          intensity={params.spot_intensity}
          castShadow={
            params.isAndroid ? false : true
          }
          shadow-mapSize-width={params.shadowmapsize}
          shadow-mapSize-height={params.shadowmapsize}
          shadow-camera-near={params.shadowNear}
          shadow-camera-far={params.shadowFar}
          shadow-radius={params.shadowblurradius}
          shadow-blurSamples={params.shadowblursamples}
          shadow-bias={params.shadowbias}
        />
      )}

      {/* <AutoFitCamera cameraOffset={cameraOffset} modelRef={modelRef} /> */}

      {/* Boolean check to avoid showing dimensions before model has loaded */}
      {!isCameraSettled ||
      !positionAndLabelOutput ||
      positionAndLabelOutput.length === 0 ? null : (
        <>
          {positionAndLabelOutput.map((line, index) => {
            return (
              <React.Fragment key={index}>
                <DimensionLine
                  params={params}
                  line_width={line_width}
                  start={line.start}
                  end={line.end}
                  visible={showLabels}
                />
                <DimensionLabel
                  position={line.label}
                  label={line.labelText}
                  visible={showLabels}
                  isMobile={isMobile}
                  // isSmall={isSmallObject}
                  inverse={sceneBackgroundColour === "black"}
                />
                <Arrowhead
                  params={params}
                  position={line.start}
                  direction={line.end}
                  visible={showLabels}
                  scaleFactor={line.scaleFactor}
                />
                <Arrowhead
                  params={params}
                  position={line.end}
                  direction={line.start}
                  visible={showLabels}
                  scaleFactor={line.scaleFactor}
                />
                {/* if values are offset, show the dotted offset lines */}
                {line.offsetstart && (
                  <>
                    <DimensionLine
                      params={params}
                      line_width={line_width}
                      start={line.offsetstart}
                      end={line.start}
                      visible={showLabels}
                      lineStyle="dotted"
                      scaleFactor={line.scaleFactor}
                    />
                    <DimensionLine
                      params={params}
                      line_width={line_width}
                      start={line.offsetend}
                      end={line.end}
                      visible={showLabels}
                      lineStyle="dotted"
                      scaleFactor={line.scaleFactor}
                    />
                  </>
                )}
              </React.Fragment>
            );
          })}
          { !coordinateData || !coordinateData.diameters ? null : 
            coordinateData.diameters.map((line, index) => {
              return (
                <React.Fragment key={index}>
                  <DimensionLine
                    params={params}
                    line_width={line_width}
                    start={line.start}
                    end={line.end}
                    visible={showLabels}
                  />
                  <DimensionLabel
                    position={line.end}
                    label={line.value}
                    visible={showLabels}
                    isMobile={isMobile}
                    // isSmall={isSmallObject}
                    inverse={sceneBackgroundColour === "black"}
                  />
                </React.Fragment>
              );
            })
          }
        </>
      )}

      {params.environmentIntensity > 0  && variantEnvironmentsHdr && variantEnvironmentsHdr.length !== 0 && (
        <Environment
          files={variantEnvironmentsHdr[0]}
          background={false} // can be true, false or "only" (which only sets the background) (default: false)
          environmentIntensity={params.environmentIntensity} // optional intensity factor (default: 1, only works with three 0.163 and up)
          environmentRotation={[0, Math.PI / 2, 0]} // optional rotation (default: 0, only works with three 0.163 and up)
        />
      )}
      {/* <OrbitControls
            ref={controls}
            enablePan={false}
            //target={[0, 0.35, 0]} //{[0, 0.35, 0]}
            //minDistance={params.zoomClose}
            //maxDistance={params.zoomFar}
            maxPolarAngle={Math.PI / 2}
        /> */}
    </>
  );
}

const ModelViewer = ({
  variantFileGlb,
  variantFileGlbUncompressed,
  variantFileUsd,
  variantFilesHdr,
  variantEnvironmentsHdr,
  yawAngle,
  pitchAngle,
  windowWidth,
  aspectRatio,
  objectDimensions,
  productTags,
  productSettings,
  configuratorEnabled,
  onMeshClicked,
  setCamTransform,
  camTransform,
  parentWidth,
  parentHeight,
  windowHeight,
}) => {
  const store = createXRStore();
  const [isAR, setIsAR] = useState(false); // Toggle AR mode
  const [isMobile, setIsMobile] = useState(false); // Track if the device is mobile
  console.log('Show dimensions:', productSettings.showDimensionsAtStart);
  const [showLabels, setShowLabels] = useState(productSettings.showDimensionsAtStart !== undefined ? productSettings.showDimensionsAtStart : true); // State for dimension labels
  const [disableLabels, setDisableLabels] = useState(false);
  const [animationPlaying, setAnimationPlaying] = useState(false);
  const [isAnimToggled, setIsAnimToggled] = useState(false);
  const [hasAnimations, setHasAnimations] = useState(false);
  const [texture, setTexture] = useState(null); // Use state to manage the loaded texture
  const toggleDimensions = () => setShowLabels(!showLabels);
  const toggleAnimation = () => {
    setAnimationPlaying(!animationPlaying);
    setIsAnimToggled(true);
    setShowLabels(false);
    setDisableLabels(!animationPlaying);
    // setAnimationDirection(-animationDirection);
  }
  const [gltfScene, setGltfScene] = useState(null);

  //AR variables
  const containerRef = useRef();
  const rendererRef = useRef();
  const sceneRef = useRef();

  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingLightMap, setLoadingLightMap] = useState(true);
  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);

  const spinnerContainerRef = useRef(null); // Create a reference for the spinner container
  const spinnerInstanceRef = useRef(null); // Store the Spinner instance

  // const [isLoadingARModel, setIsLoadingARModel] = useState(true);
  // const modelViewerRef = useRef(null);
  // const [isARAndroidStarted, setIsARAndroidStarted] = useState(false);
  // const [uncompressedGltfModel, setUncompressedGltfModel] = useState(null);
  // const [uncompressedUsdModel, setUncompressedUsdModel] = useState(null);

  // backgroundColour: "white",
  // smallObject: false,
  // arEnabled: true,
  // qrCode: true

  // Only set `isMobile` once when the component mounts
  useEffect(() => {
    setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
  }, []); // Empty dependency array ensures this runs only on mounts

  useEffect(() => {
    setForceUpdate((prev) => !prev);
    setLoadingLightMap(true);
  }, [variantFileGlb]);

  const getRootUrl = () => {
    const { protocol, host } = window.location; // Extract protocol (http/https) and host
    return `${protocol}//${host}`; // Combine them to form the root URL
  };

  useEffect(() => {
    // Prepare the iOS and Android links with scaling disabled for iOS
    const iosLink = `${variantFileUsd}#allowsContentScaling=0`;
    const androidLink = variantFileGlbUncompressed;
    //Get the website root url
    const landingPageUrl =
      getRootUrl() +
      `/ar-redirect/?iosLink=${encodeURIComponent(
        iosLink
      )}&androidLink=${encodeURIComponent(androidLink)}`;
    console.log("Landing page url:", landingPageUrl);

    // Generate QR Code for the landing page
    QRCode.toDataURL(landingPageUrl, { width: 300 })
      .then((url) => setQrCodeUrl(url))
      .catch((err) => console.error("QR Code generation failed:", err));
  }, []);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    const isAndroidDevice = /android/i.test(userAgent);
    const isiOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isMobileDevice = /Mobi|Android/i.test(userAgent);

    setIsAndroid(isAndroidDevice);
    setIsIOS(isiOSDevice);
    setIsMobile(isMobileDevice);
    setLoading(false); // Set loading to false after setting states
  }, []); // Run only on mount

  // useEffect(() => {
  //   // Load texture with startTransition to avoid blocking synchronous input
  //   startTransition(() => {
  //     const loadedTexture = new RGBELoader().load("process.env.PUBLIC_URL" + "/growm_studio.hdr", (tex) => {
  //       tex.mapping = THREE.EquirectangularReflectionMapping;
  //       setTexture(tex);
  //     });
  //   });
  // }, []);

  const params = useRef({
    //Canvas
    canvas_width: window.innerWidth,
    canvas_height: window.innerHeight,
    zoomClose: 0.25,
    zoomFar: 1,
    minZoomClose: 0.1,
    maxZoomClose: 2,
    maxZoomFar: 20,
    maxZoomFactor: 2,
    zoomOnPartMultiplier: 0.55,
    zoomMultiplier: 0.5,
    cameraOffset_desktop: 2,
    cameraOffset_mobile: 2,
    cameraFOV_desktop: 45,
    cameraFOV_mobile: 45,
    smallObjectScaleUp: 1,

    //Model
    //modelName: 'home_furniture_chair_tub',
    //modelName: 'home_furniture_sofa_BA_swift',
    //modelName: variantFileGlb[0], //It comes out as an array of length 1
    //modelName: 'home_furniture_sofa_signature.glb',
    //modelName: 'home_furniture_chair_tub.glb',
    //modelName: 'home_furniture_cabinet_animated',
    //modelName: 'electronics_kitchenaid_foodmixer.glb',
    //modelName: 'style_shoe_trainer_black.glb',
    //modelName: 'EARRING4-B.glb',
    //modelName: 'EARRING2.glb',
    //modelName: 'hex_bolt.glb',
    modelName: "demo_screw_1.glb",

    //Shadows
    shadowmapsize: 2048,
    shadowblurradius: 10,
    shadowblursamples: 15,
    shadowbias: -0.001,
    shadowNear: 1.5,
    shadowFar: 100,

    //Lights
    lightAngle: Math.PI / 5,
    lightRadius: 1.5,
    lightheight: 2.5,
    spotpenumbra: 0.5,
    spotdecay: 2,
    amblight_colour: 0xffffff,
    amblight_intensity: 0.15,
    dirlightIntensity: 0.8,
    lightMapIntensity: 3.5,
    bakedAmblight_colour: 0xffffff,
    bakedAmblight_intensity: 5,
    lights_colour: 0xffffff,
    lights_intensity: 4,
    spotLightOn: false, // Toggle for spotlight visibility
    spot_intensity: 10,
    showLabelsOn: false,

    //Environment
    floor_colour: 0xffffff,
    background_colour: 0xffffff,
    environmentIntensity: 0.35,

    //Controls
    light_position: 7,
    helpers: false,
    enablePan: false,
    fullAngle: false,

    //Lines
    line_model_offset: 0.02,
    line_floor_offset: 0.01,
    arrow_head_offset: 0.02,
    line_colour: productSettings.sceneBackgroundColour === "black" ? 0xffffff : 0x000000,
    line_width_desktop: 4,
    line_width_mobile: 2,
    line_opacity: 0.5,
    cone_opacity: 0.7,

    //Rendering Effects
    bloom_threshold: 0,
    bloom_strength: 1,
    bloom_radius: 0.5,
    saoBias: 0.5, // Increased for better effect
    saoIntensity: 0.07,
    saoScale: 0.8,
    saoKernelRadius: 1,
    saoBlur: true,
    saoBlurRadius: 10,
    saoBlurStdDev: 5,
    saoBlurDepthCutoff: 0.01,

    //OS
    isAndroid: isAndroid,
  });

  // Update the params object when isAndroid changes
  useEffect(() => {
    params.current.isAndroid = isAndroid;
  }, [isAndroid]); // Runs every time isAndroid changes

  useEffect(() => {
    console.log("product tags modelviewer:", productTags);
    params.current.line_colour =
      productSettings.sceneBackgroundColour === "black" ? 0xffffff : 0x000000;
    if (productTags.includes("jewellery")) {
      params.current.modelScale = 10;
      params.current.environmentIntensity = 1;
      params.current.enablePan = true;
      params.current.fullAngle = true;
    } else {
      //Non exclusive if statements so we can accept several tags, glass and reflective override fabrics
      if (productTags.includes("fabrics")) {
        console.log("fabrics");
        params.current.environmentIntensity = 0;
      }
      if (productTags.includes("reflective")) {
        console.log("reflective");
        params.current.environmentIntensity = 1;
      }
      if (productTags.includes("glass")) {
        console.log("glass");
        params.current.environmentIntensity = 1;
        params.current.enablePan = true;
        params.current.fullAngle = true;
      }
      if (productTags.includes("freeCam")) {
        console.log("free cam");
        params.current.enablePan = true;
        params.current.fullAngle = true;
      }
      if (productTags.includes("smallObject")) {
        params.current.environmentIntensity = 0.75;
      }
    }
  }, [productTags, productSettings]);

  const cameraOffset = isMobile
    ? params.current.cameraOffset_mobile
    : params.current.cameraOffset_desktop;
  const cameraFOV = isMobile
    ? params.current.cameraFOV_mobile
    : params.current.cameraFOV_desktop;
  const line_width = isMobile
    ? params.current.line_width_mobile
    : params.current.line_width_desktop;

  const triggerAR = async () => {
    if (
      navigator.xr &&
      (await navigator.xr.isSessionSupported("immersive-ar"))
    ) {
      //const session = await navigator.xr.requestSession('immersive-ar', { requiredFeatures: ['hit-test'] });
      const session = await navigator.xr.requestSession("immersive-ar", {
        requiredFeatures: ["hit-test"],
      });

      const renderer = new THREE.WebGLRenderer({ alpha: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.xr.enabled = true;
      document.body.appendChild(renderer.domElement);

      if (
        containerRef.current &&
        !containerRef.current.contains(renderer.domElement)
      ) {
        containerRef.current.appendChild(renderer.domElement);
      }
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(
        70,
        window.innerWidth / window.innerHeight,
        0.01,
        20
      );
      const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1);
      scene.add(light);

      // Load the GLTF model and add it to the scene
      const loader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        "https://cdn.jsdelivr.net/npm/three@0.153.0/examples/jsm/libs/draco/gltf/"
      );
      loader.setDRACOLoader(dracoLoader);
      let model;

      loader.load(
        variantFileGlb[0],
        (gltf) => {
          model = gltf.scene;
          model.scale.set(1, 1, 1);
          model.position.set(0, 0, -2);
          sceneRef.current.add(model);
        },
        undefined,
        (error) => {
          console.error("Error loading GLTF model:", error);
        }
      );

      // Request reference space
      let referenceSpace = null;
      const spacesToTry = ["local-floor", "viewer", "local"];

      for (const space of spacesToTry) {
        try {
          referenceSpace = await session.requestReferenceSpace(space);
          if (referenceSpace) break; // Exit loop once a valid space is found
        } catch (error) {
          console.warn(
            `Reference space ${space} not supported. Trying next...`
          );
        }
      }

      if (!referenceSpace) {
        console.error("No compatible reference space found.");
        return;
      }

      // Ensure renderer uses the correct reference space
      renderer.xr.setReferenceSpace(referenceSpace);
      console.log("Session:", session);
      renderer.xr.setSession(session);

      session.addEventListener("end", () => {
        renderer.setAnimationLoop(null);
      });

      renderer.setAnimationLoop(() => {
        renderer.render(scene, camera);
      });
    } else {
      console.error("WebXR not supported");
    }
  };

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px",
      maxWidth: "90%", // Ensure it fits smaller screens
      maxHeight: "90%", // Prevent overflow on smaller screens
      boxSizing: "border-box",
    },
    overlay: {
      display: "flex", // Ensure the modal centers within the overlay
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0, 0, 0, 0.5)", // Dimmed background for better focus
      zIndex: 1000,
    },
  };

  function openModal() {
    setShowLabels(false);
    setIsOpen(true);
  }

  function afterOpenModal() {
    // Code placeholder
  }

  function closeModal() {
    setIsOpen(false);
  }

  useEffect(() => {
    // Spinner options
    const spinnerOptions = {
      lines: 12, // The number of lines to draw
      length: 0, // The length of each line
      width: 5, // The line thickness
      radius: 10, // The radius of the inner circle
      scale: 4, // Scales overall size
      corners: 1, // Corner roundness (0..1)
      color: '#000', // CSS color or array of colors
      fadeColor: 'transparent', // CSS color or array of colors
      speed: 1, // Rounds per second
      rotate: 0, // The rotation offset
      animation: 'spinner-line-fade-quick', // The CSS animation name
      direction: 1, // 1: clockwise, -1: counterclockwise
      zIndex: 2e9, // The z-index (defaults to 2000000000)
      className: 'spinner', // CSS class to assign to the spinner
      top: '50%', // Top position relative to parent
      left: '50%', // Left position relative to parent
      shadow: '0 0 1px transparent', // Box-shadow for the lines
      position: 'absolute', // Element positioning
    };

    // Create a Spinner instance
    const spinner = new Spinner(spinnerOptions);
    spinnerInstanceRef.current = spinner;

    // Mount spinner to the container
    if (spinnerContainerRef.current) {
      spinner.spin(spinnerContainerRef.current);
    }

    // Cleanup on component unmount
    return () => {
      if (spinnerInstanceRef.current) {
        spinnerInstanceRef.current.stop();
      }
    };
  }, [spinnerContainerRef.current]);

  console.log('isMobile:', isMobile, 'isAR:', isAR, 'showLabels:', showLabels);
  console.log('parent width modelviewer:', parentWidth);

  //Reality converter on mac (glb to usdz)
  return (
    <>
      {/* Spinner Overlay (Only shows when loadingLightMap is true) */}
      {loadingLightMap && (
        <div
          ref={spinnerContainerRef}
          className="app-loading"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            height: '250px',
            width: '250px',
          }}
        />
      )}
      <div className="model-viewer">
        <Modal
          isOpen={modalIsOpen}
          onAfterOpen={afterOpenModal}
          onRequestClose={closeModal}
          style={customStyles}
          contentLabel="QR Code Modal"
        >
          <div className="modal-content">
            <button onClick={closeModal} className="close-button-qr">
              <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/close.jpeg`} className="icon-qr"></img>
            </button>
            <div className="qr-code-text">
              Scan to view in AR
              <div className="qr-code-container">
                <img src={qrCodeUrl} className="qr-code" alt="Scan to view in AR" />
              </div>
            </div>
          </div>
        </Modal>
        <div style={{ width: parentWidth, height: parentHeight, visibility: loadingLightMap ? 'hidden' : 'visible' }}>
          <Suspense fallback={<div>Loading...</div>}>
            <Canvas shadows camera={{ position: [-2, 1, 2], fov: 35 }} >
              {isAR ? (
                <>
                  <XR store={store}>
                    <ARModel modelUrl={params.current.modelName + '.glb'} />
                    <Environment preset="sunset" />
                  </XR>
                </>
              ) : (
                <>
                  <ThreeScene
                    key={forceUpdate}
                    params={params.current}
                    cameraOffset={cameraOffset}
                    setCamTransform={setCamTransform}
                    camTransform={camTransform}
                    line_width={line_width}
                    showLabels={showLabels}
                    animationPlaying={animationPlaying}
                    isAnimToggled={isAnimToggled}
                    // animationDirection={animationDirection}
                    setHasAnimations={setHasAnimations}
                    setGltfScene={setGltfScene}
                    variantFileGlb={variantFileGlb}
                    variantFileUsd={variantFileUsd}
                    variantFilesHdr={variantFilesHdr}
                    variantEnvironmentsHdr={variantEnvironmentsHdr}
                    yawAngle={yawAngle}
                    pitchAngle={pitchAngle}
                    windowWidth={windowWidth}
                    windowHeight={windowHeight}
                    aspectRatio={aspectRatio}
                    objectDimensions={objectDimensions}
                    parentWidth={parentWidth}
                    parentHeight={parentWidth}
                    //TODO: Replace productTags with json in bucket
                    sceneBackgroundColour={productSettings.sceneBackgroundColour}
                    isSmallObject={productTags.includes("smallObject")}
                    dimensionsOnTop={productTags.includes("dimensionsOnTop")}
                    productSettings={productSettings}
                    configuratorEnabled={configuratorEnabled}
                    isMobile={(isAndroid || isIOS || isMobile)}
                    onMeshClicked={onMeshClicked}
                    setLoadingLightMap={setLoadingLightMap}
                    style={{
                      width: parentWidth,
                      height: parentHeight,
                    }}
                  />
                  {productTags.includes("jewellery") ? (
                    <Caustics color="#FF8F20" position={[0, -0.5, 0]} lightSource={[5, 5, -10]} worldRadius={0.01} ior={1.2} intensity={0.005}>
                      <mesh position={[-2, 0.5, -1]} scale={0.5}>
                        <MeshTransmissionMaterial resolution={1024} distortion={0.25} color="#FF8F20" thickness={1} anisotropy={1} />
                      </mesh>
                    </Caustics>
                  ) : null}
                  <EffectComposer>
                    {/* <BrightnessContrast brightness={0.1} contrast={0.15} /> */}
                    {productTags.includes("jewellery") ? (
                      <>
                        <Bloom
                          intensity={1.5}
                          luminanceThreshold={1}
                        // luminanceSmoothing={0.1}
                        // height={300}
                        //layers={diamondsLayer} //Add diamond layer
                        />
                        <ChromaticAberration offset={[0.001, 0.001]} />)
                      </>
                    ) : null}
                    {!isAndroid ?
                      <N8AO
                        intensity={5}
                        radius={0.01}
                        distanceFalloff={0.01}
                        depthAwareUpsampling={true}
                        bias={0.5}
                        strength={1}
                        dark={1}
                      />
                      : null}
                  </EffectComposer>
                  {(!showLabels && !productTags.includes("smallObject") && !productSettings.isBaked ?
                    (!isAndroid) ?
                      <AccumulativeShadows
                        temporal
                        frames={100}
                        scale={5}
                        opacity={1}
                        resolution={2048}
                      >
                        <RandomizedLight amount={2} position={[0, 1, 0]} radius={5} />
                      </AccumulativeShadows>
                      : <ContactShadows
                        width={1}
                        height={1}
                        far={8}
                        rotation={[Math.PI / 2, 0, 0]}
                      />
                    : null
                  )}
                </>
              )}
            </Canvas>
          </Suspense>
        </div>

          {/* Hide the button when not on a mobile device or on ios + show QR code. 
        TODO: Add this at higher level so we can show AR even if we don't show the scene in model viewer
        Button in Main will have to call a function that references a parameter in another component so it can trigger the scene (triggerAR for Android). 
        triggerAR and ios equivalent would need to be moved to another component entirely. activateAR isn't working!*/}
          {loading ? null : isAndroid &&
            !isIOS &&
            productSettings.isAREnabled ? null : !isIOS && // </> //   </button> //     /> //       className="icon" //       alt="View in AR Icon" //       src={`${process.env.PUBLIC_URL}/icon_viewinar.svg`}  // Path to the image in the public folder //     <img // <button onClick={triggerAR} className="ar-button"> // <>
            productSettings.qrCode ? (
            !qrCodeUrl ? (
              <p>Loading QR Code...</p>
            ) : (
              <>
                <button onClick={openModal} className="ar-button">
                  <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_viewinar.svg`} alt="View in AR" />
                </button>
              </>
            )
          ) : null}

          {productSettings.showDimensions === true ? (
            <button
              onClick={toggleDimensions}
              className={`button-dim ${showLabels ? "active" : ""}`}
              disabled={disableLabels}
            >
              <img
                src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_dimensions1.svg`}  // Path to the image in the public folder
                alt="Dimensions Icon"
                className="gromwIcon"  // Add a class for custom styling
                style={{ filter: showLabels ? 'invert(100%)' : 'none' }}
                />
              </button>
            ) : null
          }

          {/* Hide the button when not on a mobile device or on Android + show QR code. Add #allowsContentScaling=0*/}
          {
            (productSettings.isAREnabled) ?
              isIOS ?
                (
                  <a rel="ar" className="ar-button" href={`${variantFileUsd}#allowsContentScaling=0`}>
                    <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_viewinar.svg`} alt="View in AR" />
                  </a>
                )
                : (isAndroid) ? (
                  <>
                    <Suspense fallback={<div>Loading AR environment...</div>}></Suspense>
                    <a
                      className="ar-button"
                      //Disable scaling here as well
                      href={`intent://arvr.google.com/scene-viewer/1.0?file=${variantFileGlbUncompressed}&mode=ar_preferred&title=My+Model&link=https://demo.growm.io#Intent;scheme=https;package=com.google.ar.core;end`}
                    >
                      <img src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_viewinar.svg`}
                        alt="View in AR" />
                    </a>
                  </>
                ) : null
              : null
          }

          <button
            onClick={toggleAnimation}
            className={`button-anim ${hasAnimations ? '' : 'button disabled'} ${animationPlaying ? 'active' : ''}`}
            disabled={!hasAnimations}
          >
            <img
              src={`${window.ASSET_BASE_URL || process.env.PUBLIC_URL}/icon_animation.svg`}  // Path to the image in the public folder
              alt="Animations Icon"
              className="growmIcon"  // Add a class for custom styling
              style={{ filter: animationPlaying ? 'invert(100%)' : 'none' }}
            />
          </button>
        </div>
    </>
  );
};

export default ModelViewer;
